import { useCallback, useState } from 'react';
import i18n, { STORAGE_KEY, SUPPORTED_LANGUAGES, SupportedLanguage } from '../i18n';
import { fetchApi } from '../config/api';

export interface LanguageDefinition {
  id: SupportedLanguage;
  label: string;
  nativeLabel: string;
}

export const LANGUAGES: LanguageDefinition[] = [
  { id: 'de', label: 'Deutsch', nativeLabel: 'DE' },
  { id: 'en', label: 'English', nativeLabel: 'EN' },
  { id: 'fr', label: 'Français', nativeLabel: 'FR' },
  { id: 'it', label: 'Italiano', nativeLabel: 'IT' },
  { id: 'nl', label: 'Nederlands', nativeLabel: 'NL' }
];

export function getStoredLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(stored ?? '')
      ? (stored as SupportedLanguage)
      : 'de';
  } catch {
    return 'de';
  }
}

/** Wechselt die Sprache nur lokal (i18next-Instanz + localStorage). Funktioniert auch ohne
 * Anmeldung (z.B. auf der Landing Page). Serverseitige Persistierung siehe setLanguage(). */
export function applyLanguageLocally(lang: SupportedLanguage) {
  i18n.changeLanguage(lang);
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Auswahl gilt dann nur fuer diese Sitzung.
  }
}

/** Wechselt die Sprache lokal und speichert sie bei angemeldetem Nutzer zusaetzlich im Konto,
 * damit sie beim naechsten Login auf jedem Geraet uebernommen wird. */
export async function setLanguage(lang: SupportedLanguage, isAuthenticated: boolean) {
  applyLanguageLocally(lang);
  if (isAuthenticated) {
    await fetchApi('/auth/me', {
      method: 'PUT',
      body: JSON.stringify({ preferredLanguage: lang })
    });
  }
}

export function useLanguage() {
  const [language, setLanguageState] = useState<SupportedLanguage>(getStoredLanguage);

  const changeLanguage = useCallback((next: SupportedLanguage, isAuthenticated: boolean) => {
    setLanguageState(next);
    return setLanguage(next, isAuthenticated);
  }, []);

  return { language, changeLanguage };
}
